<template>
  <div class="page-max-width">
    <section class="hero-banner !tw-h-auto !tw-bg-cover">
      <div class="column">
        <h1 class="heading-1 display-font tw-mb-4">About Us</h1>
        <nuxt-img
          width="32"
          height="32"
          class="ts-sticker"
          src="/images/ts-logo-sticker-white.svg"
          alt=""
        />
      </div>
    </section>
    <section class="column">
      <div class="columns small-12 medium-11 large-8">
        <h2 class="heading-2">Our Mission</h2>
        <p class="body-copy">
          <strong>To spark more “aha” moments for every teacher and student.</strong>
        </p>
        <p class="body-copy">
          We believe every little win in the classroom is a step towards a brighter future.
        </p>
        <p class="body-copy">
          That's why we’re here for the “aha” moments. When a student lights up because they have
          learned something new. The combined triumphs that bring joy, high-fives and inspire love
          for learning.
        </p>
        <p class="body-copy">
          Our curated library of teaching resources, classroom solutions, and educator apps gives
          K-6 teachers the tools and inspiration they need to design unique and impactful classroom
          experiences.
        </p>
      </div>
    </section>

    <section id="stats" class="row align-center">
      <ul
        class="row block-grid small-up-1 large-up-3 small-11 medium-8 large-11"
        style="width: 100%"
      >
        <li>
          <div class="color-box pink">
            <p class="heading-4">
              <strong class="heading-2">145,000+</strong>
              Active Members
            </p>
          </div>
        </li>
        <li>
          <div class="color-box sage">
            <p class="heading-4">
              <strong class="heading-2">3,000,000+</strong>
              Students Reached
            </p>
          </div>
        </li>
        <li>
          <div class="color-box sherbert">
            <p class="heading-4">
              <strong class="heading-2">24</strong>
              Teachers Contributing
            </p>
          </div>
        </li>
      </ul>
    </section>
    <section class="column">
      <div class="columns small-12 medium-11 large-8">
        <h2 class="heading-2">Our Story</h2>
        <p class="body-copy">
          We launched as a newsletter for teachers in 2012 and have been busy helping unlock
          passion, purpose and potential for K-6 teachers and their students ever since.
        </p>
        <p class="body-copy">
          Operating out of Australia, the US and UK, our diverse team of teachers, designers and
          product creators find joy in our jobs so teachers can find more in theirs.
        </p>
      </div>
    </section>
    <section id="quote" class="columns">
      <div class="blockquote-container">
        <blockquote class="display-font">
          &ldquo; Your resources work! I&rsquo;ve been a member for many years and continue to
          encourage more teachers to become members! It has everything an effective teacher needs to
          encourage and provide excellent learning experiences for their students! &rdquo;
        </blockquote>
        <cite class="body-copy">Celia N</cite>
      </div>
    </section>
    <section class="column">
      <div class="columns small-12 medium-11 large-8">
        <h2 class="heading-2">Our Values</h2>
        <p class="body-copy">
          <strong>Agency:</strong>
          We believe in the power of good teachers who are armed with the best tools to teach. We
          lay the groundwork so that teachers can do the most.
        </p>
        <p class="body-copy">
          <strong>Heart:</strong>
          Teachers pour so much of themselves into their work, and we never lose sight of their
          passions. We mirror their efforts in our day-to-day.
        </p>
        <p class="body-copy">
          <strong>Impact:</strong>
          We work towards clear goals with a collective vision. We thrive when we see our actions
          positively impact teachers and students around the world.
        </p>
        <p class="body-copy">
          <strong>Simplicity:</strong>
          We create well-designed resources and ingenious solutions that are easy-to-use and fit for
          purpose. We never make things more complicated than they should be
        </p>
        <p class="body-copy">If this sounds like you, we'd love to talk!</p>
        <a
          class="button button-primary"
          href="https://teachstarter.bamboohr.com/jobs/"
          title="Join our team"
          @click="joinOurTeamClick()"
        >
          Join our team
        </a>
      </div>
    </section>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';

export default {
  name: 'About',
  data() {
    return {
      title: 'About Teach Starter',
      description:
        'Teach Starter provides you with unique, bright and engaging teaching resources to help your students or children succeed.',
    };
  },
  jsonld() {
    return {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      url: 'https://www.teachstarter.com/about',
      sameAs: [
        'https://www.facebook.com/teachstarter',
        'https://www.instagram.com/teachstarter',
        'https://www.linkedin.com/company/teach-starter',
        'https://www.youtube.com/teachstarter',
        'https://www.pinterest.com/teachstarter',
      ],
      '@id': 'https://www.teachstarter.com/#organization',
      name: 'Teach Starter',
      logo: 'https://www.teachstarter.com/images/ts-logo.png',
    };
  },
  head() {
    return {
      title: this.title,
      meta: [
        { hid: 'title', name: 'title', content: this.title },
        {
          hid: 'description',
          name: 'description',
          content: this.description,
        },
      ],
      link: this.$hrefLangsPage(this.$route),
    };
  },
  computed: {
    ...mapGetters(['treeCount', 'resourcePages']),
  },
  mounted() {
    this.$mzAnalyticsApi({
      event: 'pageView',
    });
  },
  methods: {
    joinOurTeamClick() {
      this.$mzAnalyticsApi({
        event: 'click',
        name: 'footers.about_us.join_our_team',
        productArea: 'footers',
      });
    },
  },
};
</script>

<style scoped lang="scss">
section,
.color-box,
.blockquote-container {
  padding: 2.5rem;
}
.heading-2 {
  margin: 0;
}
.heading-1 {
  text-align: center;
}

.blockquote-container {
  background-color: $pink-500;
  border-radius: $large-border-radius;
}
blockquote {
  color: $chalkboard-green-500;
  margin: 0;
}
.hero-banner {
  background-image: url('/images/patterns/rainbow_shapes.png');
  background-color: $highlighter-500;
  background-position: center;
  background-size: auto 100%;
  height: 32rem;
}

.color-box {
  display: flex;
  flex-direction: column;
  align-items: center;
  border-radius: $large-border-radius;

  p {
    text-transform: uppercase;
    text-align: center;
    &.heading-4 {
      letter-spacing: 0.09375rem;
    }
  }

  strong {
    display: block;
  }

  &.pink {
    background-color: $pink-500;
  }

  &.sage {
    background-color: $sage-500;
  }

  &.sherbert {
    background-color: $sherbert-500;
  }
}

li {
  padding: 0.5rem;
}

#quote {
  padding-top: 0;
  padding-bottom: 0;
}
#stats {
  padding: 0;
}
@media #{$medium-up} {
  blockquote {
    font-size: $new-font-size-700;
  }
  .hero-banner {
    height: 45rem;
  }
}
@media #{$large-up} {
  section,
  .blockquote-container {
    padding: 5rem;
  }

  .hero-banner {
    background-size: 100% auto;
    height: 30rem;
  }
}
.ts-sticker {
  height: 2rem;
  width: 2rem;
}
// TO DO: replace with grid classes
.column {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  height: 100%;
}
</style>
